import { useUserStore } from './user';
import useLogger from 'composables/logger';
import _uniq from 'lodash/uniq';
import { AxiosError, AxiosResponse } from 'axios';
import Errlop from 'errlop';
import { defineStore } from 'pinia';
import { api } from 'services/api';
import ErrorService from 'services/error';
import { showChangeActionDialog } from 'services/notification';
import Account from '@models/Account';

export const useAccountStore = defineStore({
  id: 'account',
  state: () => ({
    accounts: {} as { [key: string]: Account },
    accountRequests: {} as { [key: string]: Promise<AxiosResponse<ApiAccount>> },
    currentAccount: null as string | null,
    inProgress: {} as { [key: string]: boolean },
    lockStatusUpdating: false,
    invalidAccounts: {} as { [key: string]: boolean },
  }),
  getters: {
    currentAccountLocked(state) {
      return Boolean(state.accounts[<string>state.currentAccount]?.locked);
    },
  },
  actions: {
    convertAccount(accountData: ApiAccount) {
      return <Account>{
        deviceKeys: _uniq(accountData.device_keys),
        id: accountData.id,
        key: accountData.key,
        locked: accountData.locked,
        name: accountData.nickname,
        partnerKey: accountData.partner_key,
        status: accountData.status,
        userKeys: accountData.user_keys,
      };
    },
    async getAccount(key?: string | null) {
      if (!key) {
        return null;
      }

      const existingAccount = this.accounts[key];

      if (existingAccount !== undefined || this.$state.inProgress[key]) {
        // Abort if existing account already exists or a request is in progress.
        return;
      }

      let account: Account | null = null;
      try {
        this.$state.inProgress = {
          ...this.$state.inProgress,
          [key]: true,
        };
        const promise = api.get<ApiAccount>(`/account/${key}`);
        this.accountRequests[key] = promise;
        const response = await promise;
        account = this.insertAccount(response.data);

        if (key === account.id) {
          // Add a reference to this account by ID
          this.$state.accounts[account.id] = this.$state.accounts[account.key];
        }
      } catch (error) {
        const logger = useLogger();
        logger.error(`Failed to retrieve account: ${key}`, <Error>error);
        this.$patch((state) => {
          state.invalidAccounts[key] = true;
        });
      }

      this.$patch((state) => {
        delete state.inProgress[key];
        delete state.accountRequests[key];
      });

      return account;
    },
    async getOwner(key: string) {
      if (key === '') {
        return null;
      }

      const existingAccount = this.accounts[key];

      if (!existingAccount) {
        return;
      }

      let ownerKey: string | null = null;
      try {
        const response = await api.get<string | null>(`/account/${key}/owner`);
        ownerKey = response.data;
      } catch (error) {
        const logger = useLogger();
        logger.error(`Failed to retrieve owner key for account: ${key}`, <Error>error);
      }

      if (ownerKey !== null) {
        const userStore = useUserStore();
        let user = userStore.users[ownerKey] || null;

        if (!user) {
          await userStore.getUsers([ownerKey]);
          user = userStore.users[ownerKey] || null;
        }

        if (user) {
          this.$patch((state) => {
            state.accounts = {
              ...state.accounts,
              [key]: {
                ...state.accounts[key],
                owner: userStore.users[<string>ownerKey],
              },
            };
          });
        }
      }
    },
    insertAccount(accountData: ApiAccount) {
      const account = this.convertAccount(accountData);
      this.$patch((state) => {
        state.accounts = {
          ...state.accounts,
          [account.key]: account,
        };
      });
      return account;
    },
    async lockAccount({ key, lock }: { key: string; lock: boolean }) {
      let account = this.accounts[key];
      const lockOrUnlock = lock ? 'lock' : 'unlock';

      try {
        this.lockStatusUpdating = true;

        if (!account) {
          throw new Error('Account does not exist');
        }

        const response = await api.post<ApiAccount & { history_id: number }>(`/account/${key}/${lockOrUnlock}`);

        if (response.data.locked !== lock) {
          throw new Error("Unable to update this account's locked status");
        }

        account = this.convertAccount(response.data);

        this.$patch((state) => {
          state.accounts[account.key] = { ...account };
        });

        const { history_id: historyId } = response.data;

        showChangeActionDialog(
          `${account.name} ${lockOrUnlock}ed. Most changes should be reflected immediately; others will take approximately 15 minutes to be visible when reloading the page.`,
          { accountKey: account.key, historyId, icon: lock ? 'lock' : 'lock_open', timeout: 10000 }
        );
      } catch (error) {
        const status = (<AxiosError>error).response?.status;
        const message = `Failed to ${lockOrUnlock} ${account?.name}: ${status ? `(${status}) ` : ''} ${
          (<Error>error).message
        }`;
        ErrorService.notify({
          error: new Errlop(
            `Failed to toggle locked status for ${account?.name || 'account with key: ${key}'}`,
            <Error>error
          ),
          message,
        });
      } finally {
        this.$state.lockStatusUpdating = false;
      }

      return account;
    },
    setCurrentAccount(key: string) {
      this.$state.currentAccount = key;
    },
    updateAccount(accountData: Account) {
      this.$patch((state) => {
        state.accounts[accountData.key] = { ...accountData };
      });
    },
  },
});
